import { Badge, HStack } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { UserOrderByColumnEnum } from "@src/__generated__/urql-graphql";
import {
  ACTIONS_COLUMN_ID,
  Avatar,
  type BodyColumnDef,
  DataTable,
  Link,
  UserTooltip,
} from "@src/components/ui-kit";
import type { UserType } from "@src/stores/models/Me";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { last, map, upperFirst } from "lodash";
import { observer } from "mobx-react-lite";
import { useContext } from "react";
import type { UserModel } from "../models";
import { PageUsersCtx } from "../PageUsers";
import { PopoverRowActions } from "./PopoverRowActions";

type TData = {
  id: UserModel["id"];
  full_name: UserModel["full_name"];
  codename: UserModel["codename"];
  email: UserModel["email"];
  phone: UserModel["phone"];
  note: UserModel["note"];
  image: UserModel["image"];
  roles: UserModel["roles"];
  team: UserModel["team"];
  profile?: UserModel["profile"];
  brands: UserModel["brands"];
  _model: UserModel;
  archived_at: UserModel["archived_at"];
  actions: string;
  enabled_two_factor_authentication: UserModel["enabled_two_factor_authentication"];
};

const NameCell = observer<{ r: TData }>(function NameCell({ r }) {
  const { editUserModalStore } = useStore();
  const store = useContext(PageUsersCtx);

  return (
    <div className="flex items-center">
      <span style={{ marginRight: 8 }}>
        <UserTooltip user={r}>
          <Avatar
            name={r.full_name}
            src={r.image?.urls.thumbnail}
            colorScheme={r.profile?.hex_color}
          />
        </UserTooltip>
      </span>
      {can("user_create") ? (
        <button
          type="button"
          onClick={() =>
            editUserModalStore.modalState.onOpen({
              userId: r._model.id,
              userType: r._model.type,
              onSubmit: () => store.refetch?.(),
            })
          }
          className="Polaris-Link font-normal"
        >
          {r.full_name}
        </button>
      ) : (
        <span>{r.full_name}</span>
      )}

      {!!r.archived_at && (
        <Badge ml="3" colorScheme="red">
          <Trans>Deactivated</Trans>
        </Badge>
      )}
    </div>
  );
});

const getColumns = (): Record<
  UserType,
  BodyColumnDef<TData, UserOrderByColumnEnum>[]
> => {
  const commonColumns: Record<
    string,
    BodyColumnDef<TData, UserOrderByColumnEnum>
  > = {
    full_name: {
      id: "full_name",
      meta: { label: t`Name` },
      header: () => t`Name`,
      orderBy: UserOrderByColumnEnum.FullName,
      cell: ({ row }) => <NameCell r={row.original} />,
    },
    email: {
      id: "email",
      meta: { label: t`Email` },
      header: () => t`Email`,
      orderBy: UserOrderByColumnEnum.Email,
      cell: ({ row }) => row.original.email,
    },
    phone: {
      id: "phone",
      meta: { label: t`Phone` },
      header: () => t`Phone`,
      cell: ({ row }) => row.original.phone,
    },
    twoFa: {
      id: "2fa",
      meta: { label: t`Two-Factor Authentication` },
      header: () => t`Two-Factor Authentication`,
      cell: ({ row }) =>
        row.original.enabled_two_factor_authentication
          ? t`Enabled`
          : t`Disabled`,
    },
    note: {
      id: "note",
      meta: { label: t`Note` },
      header: () => t`Note`,
      cell: ({ row }) => row.original.note,
    },
    actions: {
      id: ACTIONS_COLUMN_ID,
      size: 225,
      meta: { label: t`Actions` },
      header: () => t`Actions`,
      cell: ({ row }) => <PopoverRowActions user={row.original._model} />,
    },
  };

  return {
    internal: [
      commonColumns.full_name,
      {
        id: "codename",
        meta: { label: t`Codename` },
        header: () => t`Codename`,
        cell: ({ row }) => row.original.codename,
      },
      commonColumns.email,
      commonColumns.phone,
      {
        id: "team",
        meta: { label: t`Team` },
        header: () => t`Team`,
        orderBy: UserOrderByColumnEnum.TeamName,
        cell: ({ row }) => row.original.team?.name || "",
      },
      {
        id: "roles",
        meta: { label: t`Roles` },
        header: () => t`Roles`,
        cell: ({ row }) => (
          <div className="flex items-center">
            {map(row.original.roles, (role) =>
              upperFirst(
                `${role.name.replace("-", " ")}${
                  last(row.original.roles) !== role ? ", " : ""
                }`,
              ),
            )}
          </div>
        ),
      },
      commonColumns.twoFa,
      commonColumns.note,
      commonColumns.actions,
    ],
    client: [
      commonColumns.full_name,
      commonColumns.email,
      {
        id: "brands",
        meta: { label: t`Access to brands` },
        header: () => t`Access to brands`,
        cell: ({ row }) => (
          <HStack flexWrap="wrap">
            {row.original.brands?.map((b) => (
              <HStack key={b.id}>
                <Link
                  href={{ pathname: "/brands/detail", query: { id: b.id } }}
                  maxW="130px"
                  overflow="ellipsis"
                  isTruncated
                >
                  {b.name}
                </Link>
                {last(row.original.brands)?.id === b.id ? null : (
                  <span>{", "}</span>
                )}
              </HStack>
            ))}
          </HStack>
        ),
      },
      commonColumns.phone,
      commonColumns.twoFa,
      commonColumns.note,
      commonColumns.actions,
    ],
    partner: [
      commonColumns.full_name,
      commonColumns.email,
      commonColumns.phone,
      commonColumns.twoFa,
      commonColumns.note,
      commonColumns.actions,
    ],
  };
};

export const TableUsers = observer(function TableUsers() {
  const store = useContext(PageUsersCtx);

  const data: TData[] = store.users.map((i) => ({
    id: i.id,
    full_name: i.full_name,
    codename: i.codename,
    archived_at: i.archived_at,
    email: i.email,
    phone: i.phone,
    note: i.note,
    image: i.image,
    roles: i.roles,
    team: i.team,
    profile: i.profile,
    brands: i.brands,
    _model: i,
    enabled_two_factor_authentication: i.enabled_two_factor_authentication,
    actions: "",
  }));

  return (
    <DataTable.Body
      tableKey={`${store.userType}-users`}
      data={data}
      columns={getColumns()[store.userType]}
      orderedBy={store.orderBy.clauses[0]}
      onOrderByChange={(clause) => {
        if (!clause.order) {
          store.orderBy.setClauses([]);
          return;
        }

        store.orderBy.setClauses([
          { column: clause.column, order: clause.order },
        ]);
      }}
      allIds={store.allUserIds}
      enableRowSelection
    />
  );
});
